import getDecorators from "inversify-inject-decorators";
import { SimpleGame } from "../../app";
import container from "../../ioc/inversify.config";
import TYPES from "../../ioc/types";
import { AuthService } from "../../services/auth-service";
import { AuthenticatedAssetLoader } from "../../services/authenticated-asset-loader";
import { Endpoints, SceneId } from "../../shared/enum";

const { lazyInject } = getDecorators(container);

export class BootScene extends Phaser.Scene {
    @lazyInject(TYPES.AuthService)
    private authService: AuthService;

    @lazyInject(TYPES.AuthenticatedAssetLoader)
    private assetLoader: AuthenticatedAssetLoader;

    constructor() {
        super({ key: "BootScene" });
    }

    public preload(): void {
        // Don't load game config here - it requires authentication
        // Will be loaded after login in the appropriate scene
    }

    public create(): void {
        // Show loading text while checking authentication
        const screenWidth = this.physics.world.bounds.width;
        const screenHeight = this.physics.world.bounds.height;

        const loadingText = this.add
            .text(screenWidth / 2, screenHeight / 2, "Checking authentication...", {
                fontSize: "16px",
                color: "#ffffff",
                fontFamily: "monospace",
            })
            .setOrigin(0.5);

        // Register global error handler for session-based errors
        this.registerGlobalErrorHandler();

        // Perform async authentication check
        this.checkAuthenticationAndProceed();
    }

    /**
     * Register global error handler for game_error events.
     * Listens for session-related errors (e.g., "no save selected") and redirects to load game screen.
     */
    private registerGlobalErrorHandler(): void {
        window.addEventListener("game_error:no_save_selected", (event: Event) => {
            console.warn("[BootScene] Save session lost - redirecting to load game screen");

            // Clear current game state
            SimpleGame.CURRENT_GAME = null;

            // Stop all active scenes except boot
            this.scene.manager.scenes.forEach((scene) => {
                if (scene.scene.key !== "BootScene" && scene.scene.isActive()) {
                    scene.scene.stop();
                }
            });

            // Redirect to load game screen
            this.scene.start(SceneId.LOAD_GAME_SCREEN);
        });
    }

    private async checkAuthenticationAndProceed(): Promise<void> {
        try {
            const userData = await this.authService.getCurrentUser();

            if (userData) {
                sessionStorage.setItem("user_id", userData.id);

                const projectId = SimpleGame.PROJECT_ID;
                const url = `${Endpoints.BASE_URL}/project/${projectId}/${Endpoints.GAME_CONFIGURATION}`;
                await this.assetLoader.loadJson(this, "game_config", url);

                // Load background image if configured
                const config = this.cache.json.get("game_config");
                const bgPath: string | null =
                    config?.new_game_defaults?.intro_background_image_path ?? null;
                if (bgPath) {
                    const bgUrl = `${Endpoints.ASSETS_URL}/api/v1/images/${bgPath}`;
                    try {
                        await this.assetLoader.loadImage(this, "game_background", bgUrl);
                    } catch (bgError) {
                        // Non-fatal: fall back to solid colour in scenes
                        console.warn("[BootScene] Could not load background image:", bgError);
                    }
                }

                this.scene.start(SceneId.TITLE_SCREEN);
            } else {
                this.scene.start(SceneId.LOGIN_SCREEN);
            }
        } catch (error) {
            console.error("Authentication check failed:", error);
            this.scene.start(SceneId.LOGIN_SCREEN);
        }
    }
}
